import React, {useEffect, useRef, useState} from "react";
import html2pdf from "html2pdf.js";
import {
    formatDateToDDMMYYYY,
    generateRandomNumber,
    getAuthToken,
    getClientIdOfUser,
    getLoggedInUserId,
    showToast,
} from "../../utils/utility";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {TaskCardList} from "./TaskCardView";
// Use the unified CSS file
import "../../styles/components/reports.css";
import MapLink from "../../utils/MapLink";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFilePdf, faMapLocationDot} from "@fortawesome/free-solid-svg-icons";
import ExportToExcel from "./ExportToExcel";

function TaskReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reports, setReports] = useState([]);
  const [taskCompletedList, setTaskCompletedList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch tasks whenever start/end dates change (or on initial load)
  useEffect(() => {
    fetchTasks();
  }, [startDate, endDate]);

  const taskReportRef = useRef(null);

  const handleExportPDF = () => {
    if (reports.length === 0) {
      showToast("No data available for export.", { autoClose: 3000, type: "error" });
      return;
    }

    const element = taskReportRef.current;
    const now = new Date();
    // Use ISO string replacement for a safe filename
    const formattedDate = now.toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const fileName = `TasksReport_${formattedDate}.pdf`;

    html2pdf(element, {
      margin: [0.5, 0.5],
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    });
  };

  const fetchTasks = () => {
    let endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/tasks/${UrlConstants.GET_ALL_TASKS_BY_STATUS}?taskStatus=COMPLETED`;

    if (startDate && endDate) {
      endPoint += `&startDate=${startDate}&endDate=${endDate}`;
    }

    fetchTasksFromServer(endPoint);
  };

  const fetchTasksFromServer = (endPoint) => {
    setIsLoading(true);
    fetch(`${Constants.BASE_URL}${endPoint}`, {
      method: "GET",
      headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
    })
      .then((response) => response.json())
      .then((data) => {
        const sortedTasks = (data.responseData || []).sort((a, b) => b.taskId - a.taskId);
        setTaskCompletedList(sortedTasks);
        setIsLoading(false);
      })
      .catch((error) => {
          console.error("Error fetching tasks:", error);
          setIsLoading(false);
      });
  };

  const handleCheckboxToggle = (taskId) => {
    setCheckedItems((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setReports([]);
    setCheckedItems({});
    setTaskCompletedList([]);
    fetchTasks();
  };

  const handleSearch = () => {
    if (!startDate || !endDate) {
      alert("Select Date Range (Start & End Date).");
      return;
    }
    fetchTasks();
  };

  const fetchReport = () => {
    const selectedIds = Object.keys(checkedItems).filter((taskId) => checkedItems[taskId]);

    if (selectedIds.length <= 0) {
      showToast("Please select at least one task for report generation.", { autoClose: 3000, type: "error" });
      return;
    }

    const requestBody = {
      requestId: generateRandomNumber(),
      userId: getLoggedInUserId(),
      clientId: getClientIdOfUser(),
      requestBody: { taskIds: selectedIds },
    };

    setIsLoading(true);
    fetch(`${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/task/reports`, {
      method: "POST",
      headers: {
        Authorization: `${Constants.BEARER} ${getAuthToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((data) => {
        setReports(data.responseData.taskDtoList || []);
        setIsLoading(false);
      })
      .catch((error) => {
          console.error("Error fetching report:", error);
          setIsLoading(false);
      });
  };

  // --- RENDER ROW HELPERS ---

  const renderTaskRow = (task) => {
    return (
      <React.Fragment key={task.taskId}>
        {/* Main Task Row */}
        <tr>
          <td>{task.taskId}</td>
          <td>{task.title}</td>
          <td>{task.description}</td>
          <td>{task.deviceId}</td>
          <td colSpan={2}>{task.taskStatus}</td>
        </tr>
        
        {/* Details Header Row */}
        <tr style={{background: '#f8f9fa', fontSize: '11px', color: '#666'}}>
          <td><b>Start Time</b></td>
          <td><b>End Time</b></td>
          <td><b>Assigned To</b></td>
          <td><b>Attached To</b></td>
          <td><b>All Visited</b></td>
          <td><b>Lock Tampered</b></td>
        </tr>
        
        {/* Details Value Row */}
        <tr style={{borderBottom: '2px solid #eee'}}>
          <td>{new Date(task.startTime).toLocaleString("en-IN", { timeZone: "UTC" })}</td>
          <td>{new Date(task.endTime).toLocaleString("en-IN", { timeZone: "UTC" })}</td>
          <td>{task.userAssignedTo}</td>
          <td>{task.assignedEntity || "None"}</td>
          <td>{task.allServiceLocationVisited ? "Yes" : "No"}</td>
          <td>{task.lockTampered ? "Yes" : "No"}</td>
        </tr>
        
        {/* Customer / Service Locations */}
        {task.customerList.map((customer, index) => (
             <React.Fragment key={customer.taskLocationId || index}>
                <tr>
                    <td colSpan="7" style={{padding: '10px 15px', backgroundColor: '#fdfdfd', borderBottom: '1px solid #e0e0e0'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px', color: '#2e2777', fontWeight: 'bold'}}>
                            <FontAwesomeIcon icon={faMapLocationDot} />
                            <u>Service Location {index + 1}</u>
                        </div>
                        
                        <table style={{width: '100%', fontSize: '12px', border: '1px solid #eee', marginBottom: '10px'}}>
                            <tbody>
                                <tr>
                                    <td style={{width: '15%', fontWeight: '600'}}>Address:</td>
                                    <td style={{width: '35%'}}>{customer.address}</td>
                                    <td style={{width: '15%', fontWeight: '600'}}>Phone:</td>
                                    <td style={{width: '35%'}}>{customer.phoneNumber}</td>
                                </tr>
                                <tr>
                                    <td style={{fontWeight: '600'}}>Detail:</td>
                                    <td>{customer.customerDetail}</td>
                                    <td style={{fontWeight: '600'}}>Email:</td>
                                    <td>{customer.emailId || "None"}</td>
                                </tr>
                            </tbody>
                        </table>
                        
                        {customer.geoFenceAlertList.length > 0 && (
                            <div style={{marginTop: '5px', paddingLeft: '10px', borderLeft: '3px solid #FF683A'}}>
                                <strong style={{fontSize: '11px', color: '#FF683A'}}>Geo Fence Alerts</strong>
                                <table style={{width: '100%', fontSize: '11px', marginTop: '5px'}}>
                                    <thead>
                                        <tr style={{background: '#eee'}}>
                                            <th style={{textAlign:'left', padding:'4px'}}>Type</th>
                                            <th style={{textAlign:'left', padding:'4px'}}>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customer.geoFenceAlertList.map((alert, idx) => (
                                            <tr key={idx}>
                                                <td style={{padding:'4px'}}>{alert.alertType === "GEO_FENCE_ENTRY" ? "Check-In" : "Check-Out"}</td>
                                                <td style={{padding:'4px'}}>{new Date(alert.dateTime).toLocaleString("en-IN", { timeZone: "UTC" })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </td>
                </tr>
             </React.Fragment>
        ))}

        {/* Lock/Unlock Events */}
        <tr>
          <td colSpan={7} style={{background: '#363168', color: '#fff', padding: '6px', textAlign: 'left', fontWeight: '600', fontSize: '12px'}}>
            Other Lock/Unlock Events
          </td>
        </tr>
        <tr>
          <td colSpan={7} style={{padding: '10px', backgroundColor: '#fff', borderBottom: '2px solid #ccc'}}>
            {task.lockUnlockEventList && task.lockUnlockEventList.length > 0 ? (
              <table style={{ width: "100%", fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{background: '#f1f1f1', borderBottom: '1px solid #ddd'}}>
                    <th style={{padding: '6px', textAlign:'left'}}>Report ID</th>
                    <th style={{padding: '6px', textAlign:'left'}}>Source</th>
                    <th style={{padding: '6px', textAlign:'left'}}>Time</th>
                    <th style={{padding: '6px', textAlign:'left'}}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {task.lockUnlockEventList.map((event, index) => (
                    <tr key={index} style={{borderBottom: '1px solid #eee'}}>
                      <td style={{padding: '6px'}}>{event.reportSerialNumber}</td>
                      <td style={{padding: '6px'}}>{event.eventSource}</td>
                      <td style={{padding: '6px'}}>{new Date(event.reportDateTime).toLocaleString("en-IN", { timeZone: "UTC" })}</td>
                      <td style={{padding: '6px'}}><MapLink latitude={event.latitude} longitude={event.longitude} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{textAlign: 'center', color: '#999', fontSize: '12px', padding: '5px'}}>No other events found.</div>
            )}
          </td>
        </tr>
      </React.Fragment>
    );
  };

  const renderTaskTable = () => {
    return (
      <div>
        {reports.map((report, reportIndex) => (
          <div key={reportIndex} style={{marginBottom: '40px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden'}}>
            <h4 className="filter-heading" style={{margin: 0, padding: '10px 15px', background: '#f5f5f5'}}>
                Report - {reportIndex + 1}
            </h4>
            <div style={{padding: '10px'}}>
                <table className="report-html-table">
                <thead>
                    <tr>
                    <th>Task ID</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Device ID</th>
                    <th colSpan={2}>Task Status</th>
                    </tr>
                </thead>
                <tbody>{renderTaskRow(report)}</tbody>
                </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="report-content-split">
        
        {/* MAIN AREA: Reports Display */}
        <div className="report-main-area">
            <div className="report-header-actions">
                <span style={{fontSize: '15px', fontWeight: 600, color: '#333'}}>
                    Task Report Results {reports.length > 0 && `(${reports.length})`}
                </span>
                {reports.length > 0 && (
                    <div className="export-buttons">
                        <ExportToExcel data={reports} />
                        <button className="export-btn" onClick={handleExportPDF}>
                            <FontAwesomeIcon icon={faFilePdf} style={{ color: "#FF683A" }} /> PDF
                        </button>
                    </div>
                )}
            </div>

            <div className="report-table-wrapper" ref={taskReportRef}>
                {reports.length > 0 ? (
                    renderTaskTable()
                ) : (
                    <div style={{padding: '40px', textAlign: 'center', color: '#666', marginTop: '50px'}}>
                        <p>Select completed tasks from the sidebar filters and click "Fetch Report".</p>
                    </div>
                )}
            </div>
        </div>

        {/* SIDEBAR: Filters & Selection */}
        <div className="report-filter-sidebar">
            <div className="sidebar-header">
                <h3 className="sidebar-title">Search Filters</h3>
            </div>

            <div className="sidebar-content">
                <div className="filter-group">
                    <label className="filter-label">Start Date</label>
                    <input
                        type="date"
                        className="filter-input"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
                        placeholder="Start Date"
                    />
                </div>

                <div className="filter-group">
                    <label className="filter-label">End Date</label>
                    <input
                        type="date"
                        className="filter-input"
                        onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
                        placeholder="End Date"
                    />
                </div>
                
                <div className="filter-group">
                    <button className="btn-report secondary" onClick={handleSearch}>
                        Filter List
                    </button>
                </div>

                <div className="filter-group">
                    <label className="filter-label">Completed Tasks</label>
                    <div className="asset-list-wrapper">
                        {taskCompletedList.length === 0 ? (
                            <div style={{padding: '10px', fontSize: '12px', color: '#666', textAlign:'center'}}>
                                No completed tasks found in this range.
                            </div>
                        ) : (
                            <TaskCardList
                                data={taskCompletedList}
                                checkedItems={checkedItems}
                                toggleCheckbox={handleCheckboxToggle}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="sidebar-footer">
                <div className="filter-actions">
                    <button className="btn-report" onClick={fetchReport} disabled={isLoading}>
                        {isLoading ? "Generating..." : Constants.FETCH_REPORTS}
                    </button>
                    <button className="btn-report secondary" onClick={handleClear}>
                        {Constants.CLEAR_FILTERS}
                    </button>
                </div>
            </div>
        </div>

    </div>
  );
}

export default TaskReport;